import { Injectable } from '@angular/core';
import type { Pica, PicaOptions } from 'pica';

import { ResizedImage } from '@plugins/shared/types/image';

import { readAsArrayBuffer, readAsDataUrl } from '../private/helpers';

@Injectable()
export class ImageResizeService {
  pica: Pica | null = null;

  async resize(
    source: HTMLImageElement,
    height: number,
    fileType: string,
  ): Promise<ResizedImage> {
    const pica = await this.createPica();
    const destinationCanvas = document.createElement('canvas');
    destinationCanvas.width = (height * source.width) / source.height;
    destinationCanvas.height = height;
    const result = await pica.resize(source, destinationCanvas);
    const blob = await pica.toBlob(result, fileType);
    const data = await readAsArrayBuffer(blob);

    return {
      data,
      thumb: {
        width: destinationCanvas.width,
        height: destinationCanvas.height,
        size: data.byteLength,
        type: blob.type,
      },
    };
  }

  async file2Image(file: File): Promise<HTMLImageElement> {
    const dataUrl = await readAsDataUrl(file);
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => {
        resolve();
      };
      img.onerror = () => {
        reject(new Error('Failed to load the image'));
      };
      img.src = dataUrl;
    });
    return img;
  }

  // Dynamically importing and creating a Pica instance to avoid
  // including it in the plugins' bundle by default even if it is not used.
  private async createPica(): Promise<Pica> {
    if (this.pica) {
      return this.pica;
    }
    // correct types as @types/pica seems to be wrong for this dynamic import
    const picaImport = import('pica') as unknown as Promise<{
      default: new (options: PicaOptions) => Pica;
    }>;
    this.pica = new (await picaImport).default({ features: ['js'] });
    return this.pica;
  }
}
